import { api } from '@/store/api'
import type { PaginatedResponse } from '@/types/api'

/**
 * Статус платёжной транзакции. Ручка покрывает и кредитные транзакции (ответ
 * бэка от 14.08.2026, §1); `transactionId` даёт ответ `POST ai-credits/buy`.
 *
 * `status` — СТРОКА, а не union: контракт называет всего два значения
 * (`pending`, `completed`), плеер шлёт свои, и незнакомое значение здесь
 * легально. Единственное место, где статус разбирается, — `phaseOfStatus`
 * (`lib/paymentPhase.ts`); он же приводит регистр, поэтому своего
 * `transformResponse` у ручки нет.
 *
 * Отдельный файл, а не `creditsApi.ts`: это не `ai-credits/**`, а общая
 * платёжная ручка приложения.
 */
export interface PaymentStatus {
  status: string
  message: string | null
  transactionId: number
}

export interface PaymentProduct {
  id: number
  /** Имя пакета — «200 кредитов». Картинки у пакета нет по контракту. */
  name: string
  imageUrl: string | null
  /** Число кредитов в пакете строкой («200.0000»); заполнено только у `ai_credits`. */
  credits: string | null
}

/**
 * Заказ на кредиты в истории ПЛАТЕЖЕЙ (а не движений). Способа оплаты тут нет:
 * поле `currency` живёт только в админской схеме, пользовательской достаётся
 * `priceCurrency` — валюта, которой заплатили.
 *
 * `status` — строка по той же причине, что и у статуса платежа: разбор с
 * фолбэком живёт в одном месте (`paymentStatusLabel`).
 */
export interface PaymentListItem {
  id: number
  createdAt: string
  status: string
  productType: string
  finalPrice: string
  priceCurrency: string
  paymentId: string | null
  product: PaymentProduct | null
}

export interface PaymentsArgs {
  page: number
  limit: number
}

export const paymentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPaymentStatus: build.query<PaymentStatus, number>({
      query: (transactionId) => `payments/${transactionId}/payment-status`,
    }),
    // Автор бросил оплату. Ответ не читаем: 200 бывает и с `status: error`
    // (транзакция уже не живая), а выход к витрине от исхода не зависит.
    // Теги сбрасываются при ЛЮБОМ исходе: выход гасит наблюдение, и оплата,
    // дошедшая в этот момент, иначе начислилась бы молча — автор видел бы
    // старый баланс и покупал второй раз.
    cancelPayment: build.mutation<void, number>({
      query: (transactionId) => ({ url: `payments/${transactionId}/cancel`, method: 'POST' }),
      invalidatesTags: ['CreditsBalance', 'Quote', 'CreditsHistory'],
    }),
    getMyPayments: build.query<PaginatedResponse<PaymentListItem>, PaymentsArgs>({
      query: ({ page, limit }) =>
        `users/me/transactions?type=ai_credits&page=${page}&limit=${limit}`,
      // Новый заказ появляется тут ровно тогда, когда подтверждается платёж —
      // тем же тегом, что история движений и баланс.
      providesTags: ['CreditsHistory'],
    }),
  }),
})

export const { useGetPaymentStatusQuery, useCancelPaymentMutation, useGetMyPaymentsQuery } =
  paymentsApi
